import { useEffect, useState } from 'react';
import { Modal, initials } from './parts';
import { AvatarFrame } from './Frames';
import { CoinIcon } from './Icon';
import { purchaseItem } from '../game/api';
import { track } from '../analytics';
import {
  COIN_RULES,
  EMPTY_WALLET,
  SHOP_CARDBACKS,
  SHOP_FRAMES,
  cardBackCss,
  equipCardBack,
  equipFrame,
  fetchWallet,
  savedCardBack,
  savedFrame,
  type Wallet,
} from '../game/shop';
import { useT } from '../i18n';

/** The coin store: buy + equip avatar frames and card backs (nick colours are
 * earned, not sold — see cosmetics.ts). Frame previews show the player's own
 * avatar so they can see how it looks framed. */
export function ShopModal({
  uid,
  name,
  photoURL,
  onClose,
}: {
  uid: string;
  name: string;
  photoURL: string | null;
  onClose: () => void;
}) {
  const t = useT();
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [frame, setFrame] = useState(savedFrame);
  const [back, setBack] = useState(savedCardBack);

  useEffect(() => {
    let alive = true;
    fetchWallet(uid)
      .then((w) => {
        if (!alive) return;
        setWallet(w);
        // fetchWallet reconciles this browser's local cache with the wallet
        // doc, so re-read it now to reflect what was equipped elsewhere.
        setFrame(savedFrame());
        setBack(savedCardBack());
      })
      .catch(() => alive && setWallet(EMPTY_WALLET));
    return () => {
      alive = false;
    };
  }, [uid]);

  const coins = wallet?.coins ?? 0;
  const owned = new Set(wallet?.owned ?? []);

  async function buy(itemId: string, price: number) {
    if (!wallet || busyItem) return;
    if (coins < price) {
      setError(t.shop.notEnough);
      return;
    }
    setBusyItem(itemId);
    setError(null);
    try {
      const res = await purchaseItem(itemId);
      setWallet({ ...wallet, coins: res.coins, owned: [...wallet.owned, itemId] });
      track('shop_purchase', { itemId, price });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyItem(null);
    }
  }

  /** Buy / Equip / Equipped button for one item. */
  function action(itemId: string, price: number, equipped: boolean, onEquip: () => void) {
    if (!owned.has(itemId)) {
      return (
        <button
          className="btn small amber"
          disabled={!wallet || busyItem !== null || coins < price}
          onClick={() => buy(itemId, price)}
        >
          <CoinIcon size={13} /> {price}
        </button>
      );
    }
    return equipped ? (
      <span className="shop-equipped">✓ {t.shop.equipped}</span>
    ) : (
      <button className="btn small cyan" onClick={onEquip}>
        {t.shop.equip}
      </button>
    );
  }

  return (
    <Modal onClose={onClose}>
      <div className="col" style={{ gap: 16 }}>
        <div className="row between">
          <div className="col" style={{ gap: 4 }}>
            <h2 className="h2">{t.shop.title}</h2>
            <div className="mono-dim">{t.shop.subtitle}</div>
          </div>
          <button className="link" onClick={onClose}>{t.player.close}</button>
        </div>

        <div className="shop-balance">
          <span className="mono-dim">{t.shop.balance}</span>
          <span className="shop-coins"><CoinIcon size={20} /> {wallet ? coins : '…'}</span>
        </div>
        <div className="mono-dim shop-earn-hint">
          <CoinIcon size={11} />{' '}
          {t.shop.earnHint(COIN_RULES.perGame, COIN_RULES.winBonus, COIN_RULES.mvpBonus)}
        </div>
        {error && <div className="shop-error">{error}</div>}

        <div className="col" style={{ gap: 8 }}>
          <div className="eyebrow">{t.shop.frames}</div>
          {SHOP_FRAMES.map((f) => (
            <div key={f.id} className="shop-row">
              <span className={'avatar shop-preview frame-' + f.key}>
                {photoURL ? (
                  <img className="avatar-img" src={photoURL} alt="" referrerPolicy="no-referrer" />
                ) : (
                  initials(name)
                )}
                <AvatarFrame frame={f.key} />
              </span>
              <span className="shop-name" style={{ color: f.color }}>
                {t.shop.frameNames[f.key] ?? f.key}
              </span>
              {action(f.id, f.price, frame === f.key, () => {
                equipFrame(f.key).catch(() => {});
                setFrame(f.key);
              })}
            </div>
          ))}
          {frame && (
            <button
              className="link shop-reset"
              onClick={() => {
                equipFrame(null).catch(() => {});
                setFrame(null);
              }}
            >
              {t.shop.stock}
            </button>
          )}
        </div>

        <div className="col" style={{ gap: 8 }}>
          <div className="eyebrow">{t.shop.cardbacks}</div>
          {SHOP_CARDBACKS.map((cb) => (
            <div key={cb.id} className="shop-row">
              <span className="card-back shop-preview shop-cardback" style={cardBackCss(cb.key)} />
              <span className="shop-name">{t.shop.cardbackNames[cb.key] ?? cb.key}</span>
              {action(cb.id, cb.price, back === cb.key, () => {
                equipCardBack(cb.key).catch(() => {});
                setBack(cb.key);
              })}
            </div>
          ))}
          {back && (
            <button
              className="link shop-reset"
              onClick={() => {
                equipCardBack(null).catch(() => {});
                setBack(null);
              }}
            >
              {t.shop.stock}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
